import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  Switch,
  Select,
  Space,
  Typography,
  Breadcrumb,
  Tooltip,
  theme,
} from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  CarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  ProfileOutlined,
  SendOutlined,
  AimOutlined,
  FireOutlined,
  ToolOutlined,
  DatabaseOutlined,
  DollarOutlined,
  ContactsOutlined,
  InboxOutlined,
  FileTextOutlined,
  SafetyOutlined,
  FolderOutlined,
  FileSearchOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  GlobalOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { useAppStore } from "../store/appStore";
import { useNotificationStore } from "../store/notificationStore";
import { notificationsApi } from "../api/notifications";
import {
  useRealtimeNotifications,
  useRealtimeConnectionStatus,
} from "../hooks/useRealtime";
import { disconnectEcho } from "../lib/echo";
import type { MenuProps } from "antd";
import type { Notification as AppNotification } from "../types";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const languageOptions = [
  { value: "en", label: "English" },
  { value: "sq", label: "Shqip" },
  { value: "mk", label: "Македонски" },
  { value: "de", label: "Deutsch" },
];

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const { theme: appTheme, setTheme, language, setLanguage } = useAppStore();
  const { unreadCount, notifications, markAsRead, setNotifications } =
    useNotificationStore();
  const { token } = theme.useToken();

  useRealtimeNotifications(isAuthenticated);
  const realtime = useRealtimeConnectionStatus(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectEcho();
      return;
    }

    notificationsApi
      .getNotifications({ page: 1, pageSize: 10 })
      .then((res) => setNotifications(res.data.data))
      .catch(() => {});
  }, [isAuthenticated]);

  const menuItems: MenuProps["items"] = [
    {
      key: "/dashboard",
      icon: <DashboardOutlined />,
      label: t("nav.dashboard"),
    },
    { key: "/fleet", icon: <CarOutlined />, label: t("nav.fleet") },
    { key: "/drivers", icon: <UserOutlined />, label: t("nav.drivers") },
    { key: "/routes", icon: <EnvironmentOutlined />, label: t("nav.routes") },
    { key: "/ticketing", icon: <ProfileOutlined />, label: t("nav.ticketing") },
    { key: "/dispatch", icon: <SendOutlined />, label: t("nav.dispatch") },
    { key: "/gps", icon: <AimOutlined />, label: t("nav.gps") },
    { key: "/fuel", icon: <FireOutlined />, label: t("nav.fuel") },
    { key: "/workshop", icon: <ToolOutlined />, label: t("nav.workshop") },
    {
      key: "/warehouse",
      icon: <DatabaseOutlined />,
      label: t("nav.warehouse"),
    },
    { key: "/finance", icon: <DollarOutlined />, label: t("nav.finance") },
    { key: "/crm", icon: <ContactsOutlined />, label: t("nav.crm") },
    { key: "/parcel", icon: <InboxOutlined />, label: t("nav.parcel") },
    { key: "/licenses", icon: <FileTextOutlined />, label: t("nav.licenses") },
    { key: "/aetr", icon: <SafetyOutlined />, label: t("nav.aetr") },
    { key: "/insurance", icon: <SafetyOutlined />, label: t("nav.insurance") },
    { key: "/archive", icon: <FolderOutlined />, label: t("nav.archive") },
    { key: "/dms", icon: <FileSearchOutlined />, label: t("nav.dms") },
    { key: "/hr", icon: <TeamOutlined />, label: t("nav.hr") },
    { key: "/reports", icon: <BarChartOutlined />, label: t("nav.reports") },
    { key: "/admin", icon: <SettingOutlined />, label: t("nav.admin") },
  ];

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: user ? `${user.firstName} ${user.lastName}` : "User",
      disabled: true,
    },
    { type: "divider" },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: t("common.settings"),
      onClick: () => navigate("/admin"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout"),
      danger: true,
      onClick: logout,
    },
  ];

  const handleNotificationClick = (n: AppNotification) => {
    if (!n.isRead) {
      notificationsApi.markAsRead(n.id).catch(() => {});
      markAsRead(n.id);
    }
    if (n.actionUrl) {
      navigate(n.actionUrl);
    }
  };

  const notificationItems: MenuProps["items"] = [
    ...notifications.slice(0, 5).map((n) => ({
      key: n.id,
      label: (
        <div style={{ maxWidth: 250 }}>
          <Text strong>{n.title}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {n.message}
          </Text>
        </div>
      ),
      onClick: () => handleNotificationClick(n),
    })),
    { type: "divider" },
    {
      key: "viewAll",
      label: t("common.view") + " " + t("dashboard.alerts"),
      onClick: () => navigate("/notifications"),
    },
  ];

  const handleThemeToggle = (checked: boolean) => {
    setTheme(checked ? "dark" : "light");
  };

  const handleLanguageChange = (value: string) => {
    setLanguage(value as "en" | "sq" | "mk" | "de");
    i18n.changeLanguage(value);
  };

  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    const items: { title: string; href?: string }[] = [
      { title: t("nav.dashboard"), href: "/dashboard" },
    ];

    if (pathParts.length > 0 && pathParts[0] !== "dashboard") {
      const section = pathParts[0];
      items.push({
        title: t(`nav.${section}`),
        href: `/${section}`,
      });
    }

    if (pathParts.length > 1) {
      items.push({
        title: pathParts[1].charAt(0).toUpperCase() + pathParts[1].slice(1),
      });
    }

    return items;
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
        theme="dark"
      >
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <CarOutlined style={{ fontSize: 24, color: "#fff" }} />
          {!collapsed && (
            <span
              style={{
                color: "#fff",
                fontSize: 18,
                fontWeight: 700,
                marginLeft: 12,
              }}
            >
              ERP Transport
            </span>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout
        style={{
          marginLeft: collapsed ? 80 : 256,
          transition: "margin-left 0.2s",
        }}
      >
        <Header
          style={{
            padding: "0 24px",
            background: token.colorBgContainer,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            position: "sticky",
            top: 0,
            zIndex: 99,
          }}
        >
          <Space>
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                style: { fontSize: 18, cursor: "pointer" },
                onClick: () => setCollapsed(!collapsed),
              },
            )}
          </Space>

          <Space size="middle">
            <Switch
              checked={appTheme === "dark"}
              onChange={handleThemeToggle}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
              size="small"
            />

            <Select
              value={language}
              onChange={handleLanguageChange}
              options={languageOptions}
              style={{ width: 120 }}
              size="small"
              suffixIcon={<GlobalOutlined />}
            />

            <Tooltip
              title={
                realtime.isConnected
                  ? t("realtime.connected", "Real-time connected")
                  : realtime.isConnecting
                    ? t("realtime.connecting", "Connecting…")
                    : t("realtime.disconnected", "Real-time disconnected")
              }
            >
              <Badge
                status={
                  realtime.isConnected
                    ? "success"
                    : realtime.isConnecting
                      ? "processing"
                      : "error"
                }
              />
            </Tooltip>

            <Badge count={unreadCount} size="small">
              <Dropdown
                menu={{ items: notificationItems }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <BellOutlined style={{ fontSize: 18, cursor: "pointer" }} />
              </Dropdown>
            </Badge>

            <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
              <Space style={{ cursor: "pointer" }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={user?.avatar}
                />
                <span style={{ display: "none" }}>
                  {user?.firstName} {user?.lastName}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ margin: 24, minHeight: 280 }}>
          <Breadcrumb items={getBreadcrumbs()} style={{ marginBottom: 16 }} />
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
